import { VercelKV, kv } from "@vercel/kv";

import { CacheEntities } from "./cache-types";
import crypto from "crypto";
import { retryAsync } from "../async-utils/retry-async";
import { validateCacheKey } from "./redis-cache-utils";

// This is a singleton instance of the Redis client
// VercelKV is just a Vercel-provided wrapper around Redis
let redisClient: VercelKV | null = null;

/**
 * Get Redis client instance (singleton)
 */
export function getRedisClient(): VercelKV {
  if (!redisClient) {
    redisClient = kv;
  }
  return redisClient;
}

export interface FetchWithCacheOptions {
  /** Retries after the first failed fetch (default: 3). */
  retries?: number;
}

const DEFAULT_RETRIES = 3;

/**
 * Production, preview and development deployments share one KV store, so every
 * key read or written through these helpers carries the deployment environment.
 * Without it a preview, which reads staging Tixologi, overwrites the production
 * venue page cache under the same key (TIC-926).
 */
export function scopeKeyToEnvironment(key: string): string {
  return `${key}:env:${process.env.VERCEL_ENV || "development"}`;
}

/**
 * Fetch data with Redis caching and retry logic
 * @param key Cache key
 * @param fetchFn Function to call if cache misses
 * @param ttl Time-to-live in seconds (default: 60 seconds)
 * @param options Retry budget for the fetch; a caller with its own timeout
 *   passes fewer retries so the backoff cannot outlive that timeout
 */
export async function fetchWithCache<T>(
  key: string,
  fetchFn: () => Promise<T>,
  ttl: number = 60,
  options: FetchWithCacheOptions = {}
): Promise<T> {
  const redis = getRedisClient();
  const retries = options.retries ?? DEFAULT_RETRIES;

  try {
    const validatedKey = scopeKeyToEnvironment(validateCacheKey(key));
    // Try to get from cache first
    const cachedData = await redis.get<T>(validatedKey);

    // If data exists in cache, return it
    if (cachedData !== null) {
      if (process.env.VERCEL_ENV !== "production") {
        console.log(`Cache hit for key: ${validatedKey}`);
      }
      return cachedData;
    }

    if (process.env.VERCEL_ENV !== "production") {
      console.log(`Cache miss for key: ${validatedKey}`);
    }

    // If not in cache, fetch fresh data with retry logic
    const freshData = await retryAsync(fetchFn, {
      retries,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    });

    // A stored null reads back as a miss, so writing one is a wasted round trip
    if (freshData !== null) {
      await redis.set(validatedKey, freshData, { ex: ttl });
    }

    return freshData;
  } catch (error) {
    console.error(`Redis cache error for key ${key}:`, error);
    // Fallback to direct fetch on any cache error
    return retryAsync(fetchFn, {
      retries,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    });
  }
}

/**
 * Overwrite a cache entry with a fresh value, under the same environment-scoped
 * key `fetchWithCache` reads.
 */
export async function setCache<T>(
  key: string,
  value: T,
  ttl: number = 60
): Promise<void> {
  const redis = getRedisClient();
  await redis.set(scopeKeyToEnvironment(validateCacheKey(key)), value, {
    ex: ttl,
  });
}

/**
 * Write a raw, unscoped key to the given store. For stores whose keys another
 * service reads back verbatim (the pub/sub store); use `setCache` for entries
 * `fetchWithCache` reads.
 */
export async function writeToCache<T>({
  key,
  value,
  redisClientToUse,
  ttl = 60,
}: {
  key: string;
  value: T;
  redisClientToUse: VercelKV;
  ttl?: number;
}): Promise<void> {
  await redisClientToUse.set(key, value, { ex: ttl });
}
/**
 * Invalidate a specific cache key
 * @param key Cache key to invalidate
 */
export async function invalidateCache(key: string): Promise<boolean> {
  const redis = getRedisClient();
  const sanitizedKey = scopeKeyToEnvironment(validateCacheKey(key));
  const deleted = await redis.del(sanitizedKey);

  if (deleted > 0) {
    if (process.env.VERCEL_ENV !== "production") {
      console.log(`Cache invalidated for key: ${sanitizedKey}`);
    }
    return true;
  } else {
    if (process.env.VERCEL_ENV !== "production") {
      console.log(
        `Key not found in cache: ${sanitizedKey}. Nothing to invalidate.`
      );
    }
    return false;
  }
}

/**
 * Invalidate multiple cache keys by pattern (uses Redis SCAN)
 * @param pattern Pattern to match keys (e.g. "user:*")
 */
export async function invalidateCacheByPattern(pattern: string): Promise<void> {
  const redis = getRedisClient();
  let cursor = 0;

  do {
    // Scan for keys matching pattern
    const [nextCursor, keys] = await redis.scan(cursor, {
      match: scopeKeyToEnvironment(pattern),
      count: 100,
    });

    cursor = nextCursor;

    // Delete all found keys
    if (keys.length > 0) {
      await redis.del(...keys);
      if (process.env.VERCEL_ENV !== "production") {
        console.log(
          `Invalidated ${keys.length} keys matching pattern: ${pattern}`
        );
      }
    }
  } while (cursor !== 0); // Continue until cursor is 0
}

/**
 * Generate a cache key for API data.
 *
 * Constructs a deterministic Redis cache key by combining:
 *   - A namespace/entity type (`cacheEntity`)
 *   - An identifier for the resource (`identifier`)
 *   - An optional set of parameters (`params`) sorted alphabetically
 *
 * Parameters are sorted to guarantee consistent key generation
 * regardless of object key insertion order.
 *
 * @param cacheEntity - The cache namespace or entity type (see `CacheEntities`).
 * @param identifier  - The identifier for the resource (e.g. "123" or "slug").
 * @param params      - Optional key/value pairs to include in the key; keys will be sorted alphabetically.
 * @returns A fully-qualified cache key string in the format `{cacheEntity}:{identifier}{:paramsString}`.
 */
export function generateCacheKey(
  cacheEntity: CacheEntities,
  identifier: string,
  params?: Record<string, any>
): string {
  // Sort param keys for consistent ordering
  const sortedParams = Object.keys(params || {})
    .sort()
    .reduce(
      (result, key) => {
        result[key] = params?.[key];
        return result;
      },
      {} as Record<string, any>
    );

  // Create a deterministic string from the params
  const paramsString =
    Object.keys(sortedParams).length > 0
      ? `:${JSON.stringify(sortedParams)}`
      : "";

  return `${cacheEntity}:${identifier}${paramsString}`;
}

/**
 * Generate a user scoped cache key
 *
 * This auto-hashes the user ID for security
 */
export function generateUserCacheKey(
  userId: string,
  params?: Record<string, any>
): string {
  return generateCacheKey(CacheEntities.USER, hashUserId(userId), params);
}

/**
 * Hash a Slack user ID for privacy in Redis storage
 */
export function hashUserId(userId: string): string {
  return crypto.createHash("sha256").update(userId).digest("hex");
}
